import { api } from '../../app/api';
import {
    BulkImportReportSchema,
    CalendarDiscountsSchema,
    CalendarEventSchema,
    ConsumableListSchema,
    EventListSchema,
    OrganizerHistoryListSchema,
    PaymentTypeListSchema,
    PublishMonthResultSchema,
    ReservationListSchema,
    type BulkImportReport,
    type BulkImportRequest,
    type CalendarEvent,
    type Consumable,
    type DiscountRule,
    type EventLocation,
    type EventPayload,
    type EventRound,
    type OrganizerHistoryEntry,
    type EventStatus,
    type PaymentStatus,
    type ReservationListItem,
    type ReservationStatus,
} from './calendar.schema';

// The event detail includes consumables as `product` / `single` associations
// while `/events/:id/consumables` hydrates `.item` — normalize both to `item`.
function normalizeConsumable(raw: Record<string, unknown>): Record<string, unknown> {
    return {
        ...raw,
        item: raw.item ?? raw.product ?? raw.single ?? null,
    };
}

function normalizeEvent(raw: unknown): CalendarEvent {
    const event = raw as Record<string, unknown>;
    const consumables = Array.isArray(event.consumables)
        ? event.consumables.map((c) => normalizeConsumable(c as Record<string, unknown>))
        : event.consumables;
    return CalendarEventSchema.parse({ ...event, consumables });
}

export type ListEventsArgs = {
    startDate: string;
    endDate: string;
    // Omit to list across all shops (the API treats location as an optional filter).
    location?: EventLocation;
};

export type PdfOptionsArgs = {
    date: string; // YYYY-MM
    location: EventLocation;
    headerSpace: boolean;
    sideSpace: boolean;
    days: string; // comma-separated english day names
};

export type CreateReservationArgs = {
    eventId: number;
    name: string;
    email: string;
    phone: string;
    paymentTypeId: number | null;
    amount: number | null;
    notes: string | null;
};

const calendarApi = api.injectEndpoints({
    endpoints: (builder) => ({
        listEvents: builder.query<CalendarEvent[], ListEventsArgs>({
            query: ({ startDate, endDate, location }) => ({
                url: '/events',
                method: 'GET',
                params: {
                    _start: 0,
                    _end: 500,
                    _sort: 'start',
                    _order: 'ASC',
                    startDate,
                    endDate,
                    location,
                },
            }),
            transformResponse: (raw: unknown) => EventListSchema.parse(raw).data,
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({ type: 'CalendarEvent' as const, id })),
                          { type: 'CalendarEvent' as const, id: 'LIST' },
                      ]
                    : [{ type: 'CalendarEvent' as const, id: 'LIST' }],
        }),

        getEvent: builder.query<CalendarEvent, number>({
            query: (id) => ({ url: '/events/[id]', method: 'GET', query: { id } }),
            transformResponse: (raw: unknown) =>
                normalizeEvent((raw as { data: unknown }).data),
            providesTags: (_r, _e, id) => [{ type: 'CalendarEvent', id }],
        }),

        // Same tag as the event detail — organizer edits land through
        // updateEvent, which invalidates it and refetches the trail.
        getOrganizerHistory: builder.query<OrganizerHistoryEntry[], number>({
            query: (id) => ({
                url: '/events/[id]/organizer-history',
                method: 'GET',
                query: { id },
            }),
            transformResponse: (raw: unknown) => OrganizerHistoryListSchema.parse(raw).data,
            providesTags: (_r, _e, id) => [{ type: 'CalendarEvent', id }],
        }),

        createEvent: builder.mutation<CalendarEvent, EventPayload>({
            query: (body) => ({ url: '/events', method: 'POST', body }),
            transformResponse: (raw: unknown) =>
                normalizeEvent((raw as { data: unknown }).data),
            invalidatesTags: [{ type: 'CalendarEvent', id: 'LIST' }],
        }),

        updateEvent: builder.mutation<CalendarEvent, { id: number } & EventPayload>({
            query: ({ id, ...body }) => ({
                url: '/events/[id]',
                method: 'PUT',
                query: { id },
                body,
            }),
            transformResponse: (raw: unknown) =>
                normalizeEvent((raw as { data: unknown }).data),
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'CalendarEvent', id },
                { type: 'CalendarEvent', id: 'LIST' },
            ],
        }),

        deleteEvent: builder.mutation<void, number>({
            query: (id) => ({ url: '/events/[id]', method: 'DELETE', query: { id } }),
            invalidatesTags: (_r, _e, id) => [
                { type: 'CalendarEvent', id },
                { type: 'CalendarEvent', id: 'LIST' },
            ],
        }),

        // Quick organizer swap from the event view — validated + audited
        // server-side, without the full-PUT side effects.
        updateEventOrganizer: builder.mutation<
            CalendarEvent,
            { id: number; organizerUserId: number | null }
        >({
            query: ({ id, organizerUserId }) => ({
                url: '/events/[id]/organizer',
                method: 'PATCH',
                query: { id },
                body: { organizerUserId },
            }),
            transformResponse: (raw: unknown) =>
                normalizeEvent((raw as { data: unknown }).data),
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'CalendarEvent', id },
                { type: 'CalendarEvent', id: 'LIST' },
            ],
        }),

        updateEventStatus: builder.mutation<CalendarEvent, { id: number; status: EventStatus }>({
            query: ({ id, status }) => ({
                url: '/events/[id]/status',
                method: 'PATCH',
                query: { id },
                body: { status },
            }),
            transformResponse: (raw: unknown) =>
                normalizeEvent((raw as { data: unknown }).data),
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'CalendarEvent', id },
                { type: 'CalendarEvent', id: 'LIST' },
            ],
        }),

        updateEventRounds: builder.mutation<
            CalendarEvent,
            {
                id: number;
                rounds: EventRound[];
                defaultRound?: number;
                defaultBreak?: number;
                audioVersion?: number;
            }
        >({
            query: ({ id, rounds, defaultRound, defaultBreak, audioVersion }) => ({
                url: '/events/[id]/rounds',
                method: 'PATCH',
                query: { id },
                body: { rounds, defaultRound, defaultBreak, audioVersion },
            }),
            transformResponse: (raw: unknown) =>
                normalizeEvent((raw as { data: unknown }).data),
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'CalendarEvent', id },
                { type: 'CalendarEvent', id: 'LIST' },
            ],
        }),

        finalizeEvent: builder.mutation<CalendarEvent, number>({
            query: (id) => ({
                url: '/events/[id]/finalize',
                method: 'POST',
                query: { id },
                body: {},
            }),
            transformResponse: (raw: unknown) =>
                normalizeEvent((raw as { data: unknown }).data),
            invalidatesTags: (_r, _e, id) => [
                { type: 'CalendarEvent', id },
                { type: 'CalendarEvent', id: 'LIST' },
            ],
        }),

        publishMonth: builder.mutation<
            { published: number; failed: number },
            { startDate: string; endDate: string; location: EventLocation }
        >({
            query: (body) => ({ url: '/events/publish-month', method: 'POST', body }),
            transformResponse: (raw: unknown) => PublishMonthResultSchema.parse(raw).data,
            invalidatesTags: [{ type: 'CalendarEvent', id: 'LIST' }],
        }),

        // Calendar-level (global) discount rules. Tagged with a reserved id on
        // the existing CalendarEvent tag type — the shared api's tagTypes list
        // is outside this feature, and the rules are calendar-scoped anyway.
        getCalendarDiscounts: builder.query<DiscountRule[], void>({
            query: () => ({ url: '/events-discounts', method: 'GET' }),
            transformResponse: (raw: unknown) => CalendarDiscountsSchema.parse(raw).discounts,
            providesTags: [{ type: 'CalendarEvent', id: 'DISCOUNTS' }],
        }),

        updateCalendarDiscounts: builder.mutation<DiscountRule[], DiscountRule[]>({
            query: (discounts) => ({
                url: '/events-discounts',
                method: 'PUT',
                body: { discounts },
            }),
            transformResponse: (raw: unknown) => CalendarDiscountsSchema.parse(raw).discounts,
            invalidatesTags: [{ type: 'CalendarEvent', id: 'DISCOUNTS' }],
        }),

        bulkImportEvents: builder.mutation<BulkImportReport, BulkImportRequest>({
            query: (body) => ({ url: '/events/bulk-import', method: 'POST', body }),
            transformResponse: (raw: unknown) =>
                BulkImportReportSchema.parse((raw as { data: unknown }).data),
            // Only the real run mutates the DB; the dry-run simulation must not
            // bust the calendar cache.
            invalidatesTags: (_r, _e, arg) =>
                arg.dryRun ? [] : [{ type: 'CalendarEvent', id: 'LIST' }],
        }),

        // GET-with-mutation on purpose: the blob must never enter the cache.
        downloadCalendarPdf: builder.mutation<Blob, PdfOptionsArgs>({
            query: (params) => ({
                url: '/events/pdf/generate',
                method: 'GET',
                params,
                opts: { responseType: 'blob' },
            }),
        }),

        listEventConsumables: builder.query<Consumable[], number>({
            query: (eventId) => ({
                url: '/events/[eventId]/consumables',
                method: 'GET',
                query: { eventId },
            }),
            transformResponse: (raw: unknown) => ConsumableListSchema.parse(raw).data,
            providesTags: (_r, _e, eventId) => [
                { type: 'CalendarEventConsumables', id: eventId },
            ],
        }),

        saveEventConsumables: builder.mutation<
            Consumable[],
            { eventId: number; consumables: Array<Pick<Consumable, 'id' | 'itemType' | 'itemId' | 'quantity'>> }
        >({
            query: ({ eventId, consumables }) => ({
                url: '/events/[eventId]/consumables',
                method: 'PUT',
                query: { eventId },
                body: { consumables },
            }),
            transformResponse: (raw: unknown) => ConsumableListSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { eventId }) => [
                { type: 'CalendarEventConsumables', id: eventId },
                { type: 'CalendarEvent', id: eventId },
            ],
        }),

        listReservations: builder.query<
            { data: ReservationListItem[]; count: number },
            {
                start?: number;
                limit?: number;
                q?: string;
                status?: ReservationStatus;
                paymentStatus?: PaymentStatus;
                waitlisted?: boolean;
            }
        >({
            query: ({ start = 0, limit = 30, q, status, paymentStatus, waitlisted }) => ({
                url: '/event-reservations',
                method: 'GET',
                params: {
                    _start: start,
                    _end: start + limit,
                    _sort: 'createdAt',
                    _order: 'DESC',
                    ...(q ? { q } : {}),
                    ...(status ? { status } : {}),
                    ...(paymentStatus ? { paymentStatus } : {}),
                    ...(waitlisted !== undefined ? { waitlisted: String(waitlisted) } : {}),
                },
            }),
            transformResponse: (raw: unknown) => {
                const parsed = ReservationListSchema.parse(raw);
                return { data: parsed.data, count: parsed.count ?? parsed.data.length };
            },
            providesTags: [{ type: 'EventReservation', id: 'LIST' }],
        }),

        createReservation: builder.mutation<void, CreateReservationArgs>({
            query: (body) => ({ url: '/event-reservations', method: 'POST', body }),
            invalidatesTags: (_r, _e, { eventId }) => [
                { type: 'CalendarEvent', id: eventId },
                { type: 'CalendarEvent', id: 'LIST' },
                { type: 'EventReservation', id: 'LIST' },
            ],
        }),

        updateReservationStatus: builder.mutation<
            void,
            {
                id: number;
                eventId: number;
                status?: ReservationStatus;
                paymentStatus?: PaymentStatus;
                waitlisted?: boolean;
            }
        >({
            query: ({ id, status, paymentStatus, waitlisted }) => ({
                url: '/event-reservations/[id]/status',
                method: 'PATCH',
                query: { id },
                body: { status, paymentStatus, waitlisted },
            }),
            invalidatesTags: (_r, _e, { id, eventId }) => [
                { type: 'CalendarEvent', id: eventId },
                { type: 'CalendarEvent', id: 'LIST' },
                { type: 'EventReservation', id: 'LIST' },
                { type: 'EventReservation', id },
                // A refund mark may create a storno — refresh invoice lists too.
                { type: 'Invoice', id: 'LIST' },
            ],
        }),

        deleteReservation: builder.mutation<void, { id: number; eventId: number }>({
            query: ({ id }) => ({
                url: '/event-reservations/[id]',
                method: 'DELETE',
                query: { id },
            }),
            invalidatesTags: (_r, _e, { eventId }) => [
                { type: 'CalendarEvent', id: eventId },
                { type: 'CalendarEvent', id: 'LIST' },
                { type: 'EventReservation', id: 'LIST' },
            ],
        }),

        listPaymentTypes: builder.query<Array<{ id: number; name: string }>, void>({
            query: () => ({ url: '/payment-types', method: 'GET' }),
            transformResponse: (raw: unknown) => PaymentTypeListSchema.parse(raw).data,
        }),
    }),
});

export const {
    useListEventsQuery,
    useLazyListEventsQuery,
    useBulkImportEventsMutation,
    useListReservationsQuery,
    useGetEventQuery,
    useGetOrganizerHistoryQuery,
    useCreateEventMutation,
    useUpdateEventMutation,
    useUpdateEventOrganizerMutation,
    useDeleteEventMutation,
    useUpdateEventStatusMutation,
    useUpdateEventRoundsMutation,
    useFinalizeEventMutation,
    usePublishMonthMutation,
    useGetCalendarDiscountsQuery,
    useUpdateCalendarDiscountsMutation,
    useDownloadCalendarPdfMutation,
    useListEventConsumablesQuery,
    useSaveEventConsumablesMutation,
    useCreateReservationMutation,
    useUpdateReservationStatusMutation,
    useDeleteReservationMutation,
    useListPaymentTypesQuery,
} = calendarApi;
